import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FlightItemComponent } from '../flight-item/flight-item.component';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivityRecordService } from '@shared/services/activity-record/activity-record.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import { Router } from '@angular/router';
import { LogbookLeg } from '@shared/models/activity-summary/logbook-leg';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { combineLatest, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { RippleModule } from 'primeng/ripple';
import { FlightsService } from '../services/flight.service';
import { SummaryPeriod } from '@shared/models/summary-period';
import { DateTime } from 'luxon';
import { FormsModule } from '@angular/forms';
import { ActivitySummaryResponse } from '@shared/models/activity-summary/activity-summary.response';
import { SkeletonModule } from 'primeng/skeleton';
import { AppSettings } from '@shared/models/settings/app-settings';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { FlightsToolbarComponent } from 'app/features/flights/flight-list/flights-toolbar/flights-toolbar.component';
import { formatTime } from '@shared/utilities/utilities';
import { DutyPeriodSummaries } from '@shared/models/activity-summary/duty-period-summaries';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';

@Component({
    selector: 'app-flight-list',
    imports: [
        CommonModule,
        FormsModule,
        FlightItemComponent,
        RippleModule,
        DatePickerModule,
        SkeletonModule,
        FlightsToolbarComponent,
        ButtonModule
    ],
    templateUrl: './flight-list.component.html',
    styleUrl: './flight-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightListComponent implements OnInit, OnDestroy {

  @ViewChildren('leg') legElements?: QueryList<ElementRef>;
  @ViewChildren('calendar') calendar?: QueryList<DatePicker>;
  @ViewChild('scroll') flightListScroll!: ElementRef;
  activityRecordList$ = this.loadActvityRecordList()
  filterChanged = this.signalService.settingsChanged$();
  appSettings = this.flightService.getCurrentAppSettings();
  date: Date | undefined;
  isTimeFormatUTC: boolean = false;
  lastUpdated?: string;
  calPlaceholder = '';
  unfilteredSequences: Sequences[] = [];
  destroy$ = new Subject<void>();
  minDate = new Date(1997, 11, 1);

  constructor(
    @Inject(DOCUMENT) public document: Document,
    public activityService: ActivityRecordService,
    private signalService: SignalsService,
    private flightService: FlightsService,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.removeBreadcrumb(['Sequence', 'Flight', 'Calendar']);
    if (this.appSettings) {
      this.isTimeFormatUTC = this.appSettings.dateTimeFormatDefault === 'utc' ? true : false;
    }

    const initializeDate = this.flightService.getIntialDate();
    const prevSelectedPeriod = localStorage.getItem(
      this.flightService.STORAGE_KEYS.prevSelectedPeriod
    );
    
    if (prevSelectedPeriod) {
      const selected = JSON.parse(prevSelectedPeriod);

      this.date = new Date(selected.date.year, selected.date.period - 1);
      this.flightService.setSelectedMonth(selected.date);
    } else {
      const period = DateTime.now().month;
      const year = DateTime.now().year;
      this.date = new Date(year, period - 1);
      localStorage.setItem(this.flightService.STORAGE_KEYS.prevSelectedPeriod,
        JSON.stringify({ date: { year, period } }));
      this.flightService.setSelectedMonth(initializeDate);
    }

  }

   /**
   * Observable stream that processes the activity record list from the flight service.
   * 
   * This stream performs the following operations:
   * 1. Taps into the stream to load the last updated timestamp from the activity record list.
   * 2. Switches to a new observable that combines the activity record list and settings changed action.
   * 3. Maps the combined result to:
   *    - Deep copy the activity record list to avoid mutating the original list.
   *    - Sort and filter the sequences based on application settings.
   *    - Update the application settings to ensure filter icons are correct on the toolbar.
   *    - Filter the sequences to include only those with duty period summaries that have legs matching the filter criteria.
   *    - Return the sorted and filtered list.
   * 
   * @observable
   * @type {Observable<ActivitySummaryResponse | null>}
   */
  loadActvityRecordList(): Observable<ActivitySummaryResponse | null> {
    return this.flightService.activityRecordList$.pipe(
      tap((activityRecordList) => {
        this.loadLastUpdted(activityRecordList.response?.timeStamp || '');
      }),
      switchMap((activityRecordList) => {
        if (!activityRecordList || !activityRecordList.response || activityRecordList.isLoading) {
          return of(null);
        }
  
        return combineLatest([
          of(activityRecordList),
          this.flightService.settingsChangedAction$
        ]).pipe(
          map(([activityRecordList, appSettings]) => {
            if (activityRecordList.response && !activityRecordList.isLoading) {
              // Deep copy the list to avoid mutating the original, unfiltered list sent to sequence detail
              const copyList: ActivitySummaryResponse = JSON.parse(JSON.stringify(activityRecordList.response));
              this.unfilteredSequences = this.sortedList(copyList, appSettings).sequences || [];
            }
            this.appSettings = appSettings; //update so that filter icons are correct on toolbar
            //Deep copy the list to avoid mutating the original
            const summaryRecordsString = JSON.stringify(activityRecordList.response);
            const summaryRecordsCopy: ActivitySummaryResponse = JSON.parse(summaryRecordsString);
            summaryRecordsCopy.sequences = summaryRecordsCopy.sequences.filter((sequence) => {
              sequence.dutyPeriodSummaries.forEach((dutySummary) => {
                let filteredLegs: LogbookLeg[] = [];
                this.appSettings = appSettings;
                this.isTimeFormatUTC = appSettings.dateTimeFormatDefault === 'utc' ? true : false;
                dutySummary.legs.forEach((leg) => {
                  if (appSettings.filter.flights && !leg.isDeadhead && !leg.isCancelled) {
                    filteredLegs.push(leg);
                  }
                  if (appSettings.filter.deadhead && leg.isDeadhead && !leg.isCancelled) {
                    filteredLegs.push(leg);
                  }
                  if (appSettings.filter.cancelled && leg.isCancelled) {
                    filteredLegs.push(leg);
                  }
                });
                dutySummary.legs = filteredLegs;
              });
              // Return true if any dutyPeriodSummaries have legs, otherwise false
              return sequence.dutyPeriodSummaries.some(dutySummary => dutySummary.legs.length > 0);
            });
            const sortedList = summaryRecordsCopy ? this.sortedList(summaryRecordsCopy, appSettings) : null;
            return sortedList;
          })
        );
      })
    );
  }

  /**
   * Lifecycle hook that is called after a component's view has been fully initialized.
   * This method retrieves the saved scroll position from the session storage and scrolls
   * the flight list to that position if it exists.
   */
  ngAfterViewInit(): void {
    const savedScrollPosition = sessionStorage.getItem('scrollPosition');
    if (savedScrollPosition) {
      const scrollPosition = parseInt(savedScrollPosition, 10);
      this.flightListScroll.nativeElement.scrollTo(0, scrollPosition);
    }
  }

  saveFlightListScroll(): void {
    const scrollPosition = this.flightListScroll?.nativeElement?.scrollTop ?? 0;
    this.flightService.saveScrollPosition(scrollPosition);
  }

  /**
   * Advances the current date to the next period and updates the component state accordingly.
   * 
   * This method performs the following actions:
   * 1. Retrieves the current date and constructs a `SummaryPeriod` object.
   * 2. Uses the `flightService` to get the next period based on the current date.
   * 3. Updates the component's `date` property to the new date.
   * 4. Retrieves the current application settings to determine the time format (UTC or local).
   * 5. Updates the `isTimeFormatUTC` property based on the application settings.
   * 6. Sets the selected month in the `flightService` to the new date.
   * 
   * @returns {void}
   */
  getNextPeriod(): void {
    if (this.date) {
      const currentDate: SummaryPeriod = { year: this.date.getFullYear(), period: this.date.getMonth() + 1 };
      const nextDate = this.flightService.getSelectedPeriod(currentDate);
      this.date = new Date(nextDate.year, nextDate.period - 1);
      const appSettings = this.flightService.getCurrentAppSettings();
      if (appSettings) {
        this.isTimeFormatUTC = appSettings.dateTimeFormatDefault === 'utc' ? true : false;
      }
      this.flightService.setSelectedMonth(nextDate);
    }
  }

  /**
   * Navigates to the previous period based on the current date.
   * 
   * This method checks if the `date` property is defined. If so, it calculates the 
   * previous period using the `flightService.getSelectedPeriod` method and updates 
   * the `date` property to the new period. It also sets the selected month in the 
   * `flightService` to the previous period.
   * 
   * @returns {void}
   */
  getPrevPeriod(): void {
    if (this.date) {
      const currentDate: SummaryPeriod = { year: this.date.getFullYear(), period: this.date.getMonth() + 1 };
      const prevDate = this.flightService.getSelectedPeriod(currentDate, 'prev');
      this.date = new Date(prevDate.year, prevDate.period - 1);
      this.flightService.setSelectedMonth(prevDate);
    }
  }

  onTodayClick(): void {
    const period = DateTime.now().month;
    const year = DateTime.now().year;
    this.date = new Date(year, period - 1);
    this.flightService.setSelectedMonth({ year: year, period: period });

    if (this.calendar) {
      this.calendar.forEach((calendar) => {
        calendar.overlayVisible = false;
      })
    }
  }

  onDoneClick(): void {
    if (this.calendar) {
      this.calendar.forEach((calendar) => {
        calendar.overlayVisible = false;
      })
    }
  }

  /**
   * Handles the date input event and sets the selected month and year.
   * 
   * @param event - The date event object.
   * 
   * This method checks if the time format is in UTC. If it is, it extracts the month and year
   * from the UTC date. Otherwise, it extracts the month and year from the local date.
   * The extracted month and year are then used to create a `selectedPeriod` object, which is
   * passed to the `setSelectedMonth` method of the `flightService`.
   */
  dateInputEvent(event: Date): void {
    let month = 0;
    let year = 0;

    if (this.isTimeFormatUTC) {

      month = (event.getUTCMonth() || 0) + 1;
      year = event.getUTCFullYear() || 0;
    }
    else {
      month = (event.getMonth() || 0) + 1;
      year = event.getFullYear() || 0;
    }
    const selectedPeriod = {
      year: year,
      period: month
    }

    this.flightService.setSelectedMonth(selectedPeriod);
  }

  loadLastUpdted(date: string): void {
    const dateNow = DateTime.now();
    const dateLastUpdated = DateTime.fromISO(date, { zone: 'utc' });
    const diffMinutes = dateNow.diff(dateLastUpdated, 'minutes').minutes;

    if (diffMinutes < 1) {
      this.lastUpdated = 'Just now';
      return;
    }
    else if (typeof diffMinutes != 'number') {
      this.lastUpdated = 'unknown';
    }
    else if (diffMinutes > 1) {
      this.lastUpdated = `${Math.floor(diffMinutes)} minutes ago`;
    }
    else {
      this.lastUpdated = undefined;
    }
  }

  /**
   * Sorts the sequences, duty periods, and legs within the given activity summary response
   * based on the origination date, start time, and scheduled departure time respectively.
   * The sorting order can be controlled by the `sortOrder` property in the optional `appSettings` parameter.
   *
   * @param activitySummaryResponse - The activity summary response object containing sequences to be sorted.
   * @param appSettings - Optional settings that may contain the `sortOrder` property to determine the sorting order.
   *                      If `sortOrder` is 1, the sorting will be in descending order; otherwise, it will be in ascending order.
   * @returns The sorted activity summary response.
   */
  sortedList(
    activitySummaryResponse: ActivitySummaryResponse,
    appSettings?: AppSettings
  ): ActivitySummaryResponse {
    if (activitySummaryResponse) {
      activitySummaryResponse;
      activitySummaryResponse.sequences.sort((a, b) => {
        const dateA = new Date(a.originationDate).getTime();
        const dateB = new Date(b.originationDate).getTime();
        return appSettings?.sortOrder === 1 ? dateB - dateA : dateA - dateB;
      });
      for (const sequence of activitySummaryResponse.sequences) {
        sequence.dutyPeriodSummaries.sort((a, b) => {
          const dateA = new Date(a.startTime.utc).getTime();
          const dateB = new Date(b.startTime.utc).getTime();
          return appSettings?.sortOrder === 1 ? dateB - dateA : dateA - dateB;
        });
        for (const duty of sequence.dutyPeriodSummaries) {
          duty.legs.sort((a, b) => {
            const dateA = new Date(a.departure.scheduled.utc).getTime();
            const dateB = new Date(b.departure.scheduled.utc).getTime();
            return appSettings?.sortOrder === 1 ? dateB - dateA : dateA - dateB;
          });
        }
      }
    }
    return activitySummaryResponse;
  }

  dateNotAvailable(): boolean {
    
    if (this.date?.getFullYear() === 1997 && this.date?.getMonth() + 1 === 12) {
      return true;
    }
    return false;
  }

  dateIsNow(): boolean {
    const dateNowMonth = DateTime.now().month - 1;
    const dateNowYear = DateTime.now().year;
    if (this.date?.getFullYear() === dateNowYear && this.date?.getMonth() === dateNowMonth) {
      return true;
    }
    else {
      return false;
    }
  }

  getDuration(dutyPeriods: DutyPeriodSummaries[]): number {

    if (!dutyPeriods || dutyPeriods.length === 0) {
      return 0;
    }
    const totalDays = () => {
      let total = 0;
      const startTime = DateTime.fromISO(dutyPeriods[0].startTime.utc, { zone: 'utc' });
      const endTime = DateTime.fromISO(dutyPeriods[dutyPeriods.length - 1].endTime.utc, { zone: 'utc' });
      const duration = endTime.diff(startTime, 'days');
      if (duration.days < 1) {
        total = 1;
       }
       else {
         total = duration.days;
       }
      return Math.ceil(total);
    }
    return totalDays();

    // const durationDays = timeStringToDuration(sequence.duration).days;
    // const durationHours = timeStringToDuration(sequence.duration).hours;
    // const durationMinutes = timeStringToDuration(sequence.duration).minutes;
    // const durationTrip = () => {
    //   if (durationDays == 0) {
    //     return `${durationDays + 1}`;
    //   }
    //   else if (durationDays > 0 && durationHours >= 0 && durationMinutes >= 0) {
    //     return `${durationDays + 1}`;
    //   }
    //   else {
    //     return `${durationDays}`;
    //   }
    // }

    // return durationTrip();
  }

  getTripFlightTime(sequence: Sequences): string {
    const flightTimeActual = () => {
      let total = 0;
      for (const duty of sequence.dutyPeriodSummaries) {
        total += duty.legs.reduce((sum, leg) => sum + Number(leg.flightTimeActual), 0);
      }
      return total;
    }
    return formatTime(flightTimeActual());
  }

  onSequenceClicked(selectedSequence: Sequences, index: number): void {
    const unfilteredSequence = this.unfilteredSequences.filter((sequence, seqIndex) => {
      return (
        sequence.sequenceNumber === selectedSequence.sequenceNumber &&
        seqIndex === index
      );
    })

    const sequenceNum = unfilteredSequence[0].sequenceNumber;
    const effectivePeriod = unfilteredSequence[0].effectivePeriod;
    const originationDate = DateTime.fromISO(
      unfilteredSequence[0].originationDate, {zone: 'utc'}).toFormat('yyyy-MM-dd');
    // const periodYear = effectivePeriod.year;
    // const periodMonth = effectivePeriod.period;

    this.router.navigate(['sequence', sequenceNum, 'FO',originationDate]);
  }

  onLegClicked(
    selectedLeg: LogbookLeg,
    sequence: Sequences,
  ): void {

    const flightDate = this.isTimeFormatUTC ? selectedLeg.departure.scheduled.utc : selectedLeg.departure.scheduled.local;
    const formattedFltDate = DateTime.fromISO(flightDate, {zone: 'utc'}).toFormat('yyyy-MM-dd');
    const effectivePeriod = sequence.effectivePeriod;
    const originationDate = DateTime.fromISO(sequence.originationDate, {zone: 'utc'}).toFormat('yyyy-MM-dd');

    this.router.navigate([
      'sequence',
      sequence.sequenceNumber,
      effectivePeriod.year,
      effectivePeriod.period,
      originationDate,
      'flight',
      selectedLeg.flightNumber,
      selectedLeg.departureStation.code,
      selectedLeg.arrivalStation.code,
      formattedFltDate
    ])
  }

  ngOnDestroy(): void {
    this.saveFlightListScroll();
  }

  setBorder() {
    return {
      'border-left': '1px solid var(--p-surface-400)',
      'border-right': '1px solid var(--p-surface-400)',
      'border-bottom': '1px solid var(--p-surface-400)'
    }
  }
 
  calendarButton = {
    colorScheme: {
      light: {
        root: {
          primary: {
            background: '{primary-600}',
            borderColor: '{primary-600}',
            color: '{surface-0}'
          }
        }
      },
      dark: {
        root: {
          primary: {
            background: '{primary-600}',
            borderColor: '{primary-600}',
            color: '{surface-0}'
          }
        }
      }
    }
  }
  
}


